import React, { FC, ReactNode } from 'react';
import { Box, Paper, Typography, Divider } from '@mui/material';
import { MATH_FONT, Sub, useHighlight } from './equationPrimitives';
import {
  AffinityBands,
  AFFINITY_GRADES,
  GRADE_META,
  DEFAULT_AFFINITY_BANDS,
} from './bands';
import { TIER_COLORS } from './AffinityNetwork/affinity.constants';

interface Props {
  affinityBands: AffinityBands;
}

// A card linked to the equation by a shared highlight key `k`. It lights up when
// `k` is the active key (a matching symbol or sibling card is hovered) and sets
// `k` active when hovered itself — so the simple form, the full form, and both
// card rows highlight together. See HighlightProvider in equationPrimitives.
const HighlightCard: FC<{
  k: string;
  accent: string;
  symbol: ReactNode;
  title: string;
  small?: boolean;
  children: ReactNode;
}> = ({ k, accent, symbol, title, small = false, children }) => {
  const { active, setActive } = useHighlight();
  const on = active === k;
  return (
    <Paper
      onMouseEnter={() => setActive(k)}
      onMouseLeave={() => setActive(null)}
      sx={{
        p: small ? 2 : 3,
        height: '100%',
        borderTop: `4px solid ${accent}`,
        outline: on ? `2px solid ${accent}` : '2px solid transparent',
        outlineOffset: '-1px',
        boxShadow: on ? 6 : 1,
        backgroundColor: on ? 'rgba(25,118,210,0.06)' : 'var(--surface)',
        transform: on ? 'translateY(-2px)' : 'none',
        transition: 'box-shadow .15s ease, transform .15s ease, outline-color .15s ease, background-color .15s ease',
      }}
    >
      <Box className="flex items-baseline justify-between gap-2" sx={{ mb: 0.5 }}>
        <Typography fontWeight={700} sx={{ fontSize: small ? 13 : 16 }}>
          {title}
        </Typography>
        <Box
          component="span"
          sx={{ fontFamily: MATH_FONT, fontStyle: 'italic', color: accent, fontSize: small ? 13 : 15, whiteSpace: 'nowrap' }}
        >
          {symbol}
        </Box>
      </Box>
      <Typography color="var(--text-2)" sx={{ fontSize: small ? 12 : 14 }}>
        {children}
      </Typography>
    </Paper>
  );
};

// Two rows, both wired to the equation symbols in AlgorithmCard by shared keys:
//   • 6 MAIN cards = the simple equation SCORE = band(α) + width(α)·(A·Q).
//   • 16 SMALL cards = each part of the full AB-RWEA master equation. Experience
//     lives here (χ, tᵢ, τ).
// Hovering any symbol or card lights up every element carrying the same key.
export const AlgorithmExplainerCards: FC<Props> = ({ affinityBands }) => (
  <>
    <Typography sx={{ color: 'var(--text-2)', fontSize: 13, fontWeight: 600 }}>
      The score in six pieces — hover a card or an equation symbol to trace it
    </Typography>

    {/* 6 MAIN cards — the simple equation */}
    <Box
      sx={{
        display: 'grid',
        gap: 2,
        gridTemplateColumns: {
          xs: '1fr',
          sm: 'repeat(2, 1fr)',
          md: 'repeat(3, 1fr)',
          xl: 'repeat(6, 1fr)',
        },
      }}
    >
      <HighlightCard k="score" accent="#5B7CBA" symbol="SCORE" title="Score">
        The final 0–100% match for one expert × product. Affinity sets the floor;
        alignment × profession-fit fills the band above it. Bounded in [0, 1].
      </HighlightCard>

      <HighlightCard k="band" accent={TIER_COLORS.sub_profession} symbol="band(α)" title="Band">
        The floor the score starts from, chosen by affinity. The band always wins —
        a perfect fit in the wrong category can&apos;t outrank the right one.
      </HighlightCard>

      <HighlightCard k="width" accent="#C7A66A" symbol="width(α)" title="Width">
        How much room there is to reorder experts inside the same affinity band, so
        fit refines position but never overturns affinity.
      </HighlightCard>

      <HighlightCard k="alpha" accent={TIER_COLORS.L3} symbol="α" title="Affinity">
        The primary signal: does the expert actually work in this product&apos;s
        category? It picks the band; nothing climbs above its band.
        <Box className="flex flex-col gap-1.5" sx={{ pt: 1.5 }}>
          {[...AFFINITY_GRADES].reverse().map((grade) => {
            const meta = GRADE_META[grade];
            const entry = affinityBands[grade] ?? DEFAULT_AFFINITY_BANDS[grade];
            return (
              <Box key={grade} className="flex items-center gap-2">
                <Box sx={{ width: 12, height: 12, borderRadius: '3px', bgcolor: TIER_COLORS[meta.tier], flexShrink: 0 }} />
                <Typography sx={{ fontSize: 12, fontWeight: 600 }}>{meta.label}</Typography>
                <Typography color="var(--text-2)" sx={{ fontSize: 12 }}>
                  {meta.sublabel} → floor {entry.band}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </HighlightCard>

      <HighlightCard k="A" accent={TIER_COLORS.L2} symbol="A" title="Attribute alignment">
        Weighted-Jaccard overlap between the expert&apos;s attribute values and the
        product&apos;s — more shared, important attributes → higher A. Same as
        J&#8202;w below.
      </HighlightCard>

      <HighlightCard k="Q" accent={TIER_COLORS.profession} symbol="Q" title="Profession fit">
        How relevant the expert&apos;s professions are to this product, weighted by
        experience — the only term tenure feeds into. Equals the Σ term below.
      </HighlightCard>
    </Box>

    <Divider sx={{ my: 1 }} />
    <Typography sx={{ color: 'var(--text-2)', fontSize: 13, fontWeight: 600 }}>
      The full master equation, part by part
    </Typography>

    {/* 16 SMALL cards — the full equation, part by part */}
    <Box
      sx={{
        display: 'grid',
        gap: 1.5,
        gridTemplateColumns: {
          xs: '1fr',
          sm: 'repeat(2, 1fr)',
          md: 'repeat(3, 1fr)',
          lg: 'repeat(4, 1fr)',
        },
      }}
    >
      <HighlightCard small k="score" accent="#5B7CBA" symbol="S(e, p)" title="Match score">
        The final score for one expert × product. Bounded in [0, 1); higher is a
        better match.
      </HighlightCard>

      <HighlightCard small k="e" accent={TIER_COLORS.none} symbol="e" title="Expert">
        The expert (verified creator) being scored.
      </HighlightCard>

      <HighlightCard small k="p" accent={TIER_COLORS.none} symbol="p" title="Product">
        The product being scored against.
      </HighlightCard>

      <HighlightCard small k="band" accent={TIER_COLORS.sub_profession} symbol="β(α)" title="Band floor">
        Affinity picks a disjoint band the score starts from: α=1 → 0.75, 0.7 →
        0.60, 0.4 → 0.40, 0 → 0. Strictly dominates ranking.
      </HighlightCard>

      <HighlightCard small k="width" accent="#C7A66A" symbol="ω(α)" title="Band width">
        How much room the refinement term has to reorder experts within the same
        band, so affinity is never overturned.
      </HighlightCard>

      <HighlightCard small k="alpha" accent={TIER_COLORS.L3} symbol="α(e, p)" title="Category affinity">
        Does the expert work in this product&apos;s category? Exact → 1.0, family →
        0.7, broad area → 0.4, none → 0. The primary signal.
      </HighlightCard>

      <HighlightCard
        small
        k="A"
        accent={TIER_COLORS.L2}
        symbol={<><span>J</span><Sub>w</Sub></>}
        title="Alignment"
      >
        Weighted-Jaccard overlap between the product&apos;s and expert&apos;s
        attribute values, weighted by the Attribute Weights. This is A.
      </HighlightCard>

      <HighlightCard
        small
        k="Ue"
        accent={TIER_COLORS.L2}
        symbol={<><span>U</span><Sub>e</Sub></>}
        title="Expert attributes"
      >
        The expert&apos;s attribute-value vector — the union across their
        professions, refined by their Area of Focus.
      </HighlightCard>

      <HighlightCard
        small
        k="Vp"
        accent={TIER_COLORS.L2}
        symbol={<><span>V</span><Sub>p</Sub></>}
        title="Product attributes"
      >
        The product&apos;s attribute-value vector, derived from its product
        categories.
      </HighlightCard>

      <HighlightCard
        small
        k="Q"
        accent={TIER_COLORS.profession}
        symbol={<>Σ<Sub>i</Sub></>}
        title="Profession sum"
      >
        Sums each profession&apos;s experience-weighted relevance into Q, over the
        expert&apos;s N professions. This whole term is profession fit.
      </HighlightCard>

      <HighlightCard small k="N" accent={TIER_COLORS.profession} symbol="N" title="Profession count">
        How many distinct professions the expert holds — the upper bound of the
        sum.
      </HighlightCard>

      <HighlightCard
        small
        k="rho"
        accent={TIER_COLORS.profession}
        symbol={<><span>ρ</span><Sub>i</Sub></>}
        title="Profession relevance"
      >
        How well the expert&apos;s i-th profession aligns with the product — the
        same weighted-Jaccard kernel as A, per profession.
      </HighlightCard>

      <HighlightCard
        small
        k="share"
        accent={TIER_COLORS.profession}
        symbol={<><span>ρ</span><Sub>i</Sub> / Σρ</>}
        title="Relevance share"
      >
        Normalizes each profession&apos;s relevance into a share that sums to 1, so
        Q is a relevance-weighted average (immune to duplicate professions).
      </HighlightCard>

      <HighlightCard
        small
        k="chi"
        accent={TIER_COLORS.L1}
        symbol={<>χ(t<Sub>i</Sub>)</>}
        title="Experience factor"
      >
        Turns tenure into a 0–1 factor on a diminishing-returns curve:
        χ = 1 − e^(−tᵢ/τ). Each profession&apos;s experience scales its share.
      </HighlightCard>

      <HighlightCard
        small
        k="t"
        accent={TIER_COLORS.L1}
        symbol={<>t<Sub>i</Sub></>}
        title="Tenure"
      >
        The expert&apos;s years of experience in profession i.
      </HighlightCard>

      <HighlightCard small k="tau" accent={TIER_COLORS.L1} symbol="τ" title="Experience constant">
        The experience-curve constant (tuned on the Experience tab): ~5 years →
        0.63. Lower τ rewards tenure faster.
      </HighlightCard>
    </Box>

    <Divider sx={{ my: 1 }} />
    <Typography sx={{ color: 'var(--text-2)', fontSize: 13, fontWeight: 600 }}>
      One kernel, two lenses — Match vs the Industry lens
    </Typography>

    <Box
      sx={{
        display: 'grid',
        gap: 2,
        gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
      }}
    >
      <Paper sx={{ p: 2, borderTop: '4px solid #5B7CBA' }}>
        <Typography fontWeight={700} sx={{ fontSize: 13, mb: 0.5 }}>
          The lens idea
        </Typography>
        <Typography color="var(--text-2)" sx={{ fontSize: 12 }}>
          The attribute weights are inputs to the equation, not part of it. A
          &quot;lens&quot; is the same AB-RWEA kernel run with a different weight
          vector — same Jaccard, same bands, same experience curve. Match uses
          the default weights and is persisted; the Industry lens uses the
          second weight set (Attribute Weights tab → Industry lens weights) and
          is computed live per request, so tuning it needs no recompute.
        </Typography>
      </Paper>

      <Paper sx={{ p: 2, borderTop: '4px solid #4A7BA6' }}>
        <Typography fontWeight={700} sx={{ fontSize: 13, mb: 0.5 }}>
          Industry Expert
        </Typography>
        <Typography color="var(--text-2)" sx={{ fontSize: 12 }}>
          A credentialed professional in the product&apos;s field (chef,
          clinician, engineer) — peer-level authority for a core professional
          audience. The lens boosts User Tier, Skill / Qualification and
          Compliance / Risk, so these experts separate from heavy product
          users. The badge is the binary twin of the lens: professional-tier or
          regulated profession, relevant to the product, with 3+ years tenure.
        </Typography>
      </Paper>

      <Paper sx={{ p: 2, borderTop: '4px solid #9B958C' }}>
        <Typography fontWeight={700} sx={{ fontSize: 13, mb: 0.5 }}>
          Use-Case Expert
        </Typography>
        <Typography color="var(--text-2)" sx={{ fontSize: 12 }}>
          A credible practitioner who uses the product in real life (home cook,
          experienced DIYer) — reach into adjacent audiences. Strong context
          overlap (Environment, Use Setting, Frequency) keeps Match high while
          the damped Industry lens stays low: high Match + low I is exactly the
          use-case signature. Brands set their intent per brief; the Expert
          Proposals pool can filter by either type.
        </Typography>
      </Paper>
    </Box>
  </>
);

export default AlgorithmExplainerCards;
